/**
 * BillingSetupSection — the single source of truth for the per-class billing
 * configuration UI. Consumed by:
 *
 *   • AssignClassesFeeModal (dialog body)
 *   • studentcreate.tsx     (inline section on /assign/classes/create/student/:id)
 *
 * The parent owns `configs` (keyed by class_id) so state survives filter
 * changes, recommended-classes refetches, and student data refreshes — this
 * component is a controlled view over that state and does not initialise or
 * mutate anything the parent hasn't asked for.
 *
 * The shared exports (BillingConfig, emptyConfig, buildPayloadRow,
 * isConfigValid, usePreviewLines) keep the modal and the inline flow on
 * exactly the same validation and payload shape so the API cannot receive
 * different bodies from the two surfaces.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Divider,
  Stack,
  CircularProgress,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import dayjs from "dayjs";
import AppDatePicker from "../../../Components/DatePicker/AppDatePicker";
import { usePreviewAssignClassStudentBillingMutation } from "../../../Api/attoDeskApi";
import {
  BillingConfig,
  buildPayloadRow,
  dayOrdinal,
  emptyConfig,
  isConfigValid,
} from "./billingSetupHelpers";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AssignmentBillingV2Panel from "./AssignmentBillingV2Panel";

// Re-export the pure helpers so existing imports continue to work while the
// logic lives in a React-free module that unit tests can load directly.
export type { BillingConfig, BillingType } from "./billingSetupHelpers";
export { buildPayloadRow, emptyConfig, isConfigValid } from "./billingSetupHelpers";

export type BillingClass = {
  id: number;
  label: string;       // "Grade 1 · English"
  staff?: string;
  schedule?: string;   // "Monday, Wednesday, Friday · 7:00 PM–8:00 PM"
  mode?: string;       // "Hybrid · Ajax"
};

export type PreviewLine = {
  class_id: number;
  label: string;
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
};

const isValidMoney = (v: string) => v !== "" && !Number.isNaN(Number(v)) && Number(v) >= 0;

/**
 * Debounced invoice-preview hook. Fires the preview endpoint 350ms after the
 * last change and only when at least one row is set to "invoice now". A
 * cancellation flag protects against out-of-order responses from rapid edits.
 */
export const usePreviewLines = (studentId: number | string, configs: BillingConfig[]) => {
  const [preview, { isLoading }] = usePreviewAssignClassStudentBillingMutation();
  const [data, setData] = useState<{ lines: PreviewLine[]; total: number }>({ lines: [], total: 0 });
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const configsSig = JSON.stringify(configs);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const anyInvalid = configs.length === 0 || configs.some((c) => !isConfigValid(c));
    const nowRows = configs.filter((c) => c.invoice_generation_preference === "now" && isConfigValid(c));
    if (anyInvalid || nowRows.length === 0) {
      setData({ lines: [], total: 0 });
      setError(null);
      return;
    }

    let cancelled = false;
    timerRef.current = setTimeout(async () => {
      try {
        const resp: any = await preview({
          student_id: Number(studentId),
          classes: nowRows.map(buildPayloadRow),
        }).unwrap();
        if (cancelled) return;
        if (resp?.status && resp.data) {
          setData({ lines: resp.data.lines || [], total: Number(resp.data.total || 0) });
          setError(null);
        } else {
          setError(resp?.message || "Preview failed");
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.data?.message || "Preview failed");
      }
    }, 350);

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, configsSig]);

  return { data, error, isLoading };
};

// ── View ──────────────────────────────────────────────────────────────────

type Props = {
  studentId: number | string;
  classes: BillingClass[];
  configs: Record<number, BillingConfig>;
  onChange: (class_id: number, patch: Partial<BillingConfig>) => void;
  onApplyToAll?: (source: BillingConfig) => void;
  canEditBilling?: boolean;
  /** When true the component renders without its own outer card frame — used
   *  inside the dialog body where the Dialog already provides the frame. */
  frameless?: boolean;
};

export default function BillingSetupSection({
  studentId,
  classes,
  configs,
  onChange,
  onApplyToAll,
  canEditBilling = true,
  frameless,
}: Props) {
  const theme = useTheme();
  const configList = useMemo(
    () => classes.map((c) => configs[c.id]).filter(Boolean) as BillingConfig[],
    [classes, configs],
  );
  const { data: previewData, error: previewError, isLoading: isPreviewing } = usePreviewLines(studentId, configList);

  const [confirmApplyFrom, setConfirmApplyFrom] = useState<BillingConfig | null>(null);

  const Wrapper: React.ElementType = frameless ? Box : Box;
  // NOTE: never set overflow:hidden here — MUI X DatePicker's Popper renders
  // through a portal but its measurements/scroll listeners rely on the anchor
  // chain. Clipping this wrapper caused the calendar to detach from the field
  // when the surrounding page scrolled. Border-radius is preserved without
  // needing overflow:hidden because children set their own radii.
  const wrapperSx = frameless
    ? {}
    : {
        bgcolor: "#fff",
        borderRadius: "16px",
        border: "1px solid rgba(15,23,42,0.06)",
        boxShadow: "0 1px 3px rgba(15,23,42,0.03), 0 12px 40px -24px rgba(15,23,42,0.1)",
      };

  return (
    <Wrapper sx={wrapperSx}>
      {!frameless && (
        <Box sx={{
          px: { xs: 2, md: 3 }, pt: 2.5, pb: 1.75,
          borderBottom: "1px solid rgba(15,23,42,0.05)",
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha("#0ea5e9", 0.05)} 100%)`,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: "10px",
              display: "flex", alignItems: "center", justifyContent: "center",
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.7)} 100%)`,
              color: "#fff", boxShadow: `0 6px 16px -6px ${alpha(theme.palette.primary.main, 0.6)}`,
            }}>
              <ReceiptLongIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1.2 }}>
                Billing Setup
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                Fees apply per class per student — different students in the same class can carry different rates.
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Box sx={{ p: { xs: 2, md: frameless ? 0 : 3 } }}>
        <Stack spacing={2}>
          {classes.map((c) => {
            const cfg = configs[c.id];
            if (!cfg) return null;
            const feeInvalid = cfg.fee_amount !== "" && !isValidMoney(cfg.fee_amount);
            const periodTotalInvalid = cfg.period_total_fee !== "" && !isValidMoney(cfg.period_total_fee);
            const complete = isConfigValid(cfg);
            const disable = !canEditBilling;
            const perInstallment = cfg.period_installment_count > 0 && isValidMoney(cfg.period_total_fee)
              ? Number(cfg.period_total_fee) / cfg.period_installment_count
              : null;

            const isDone = !!cfg.is_completed;
            const canComplete = complete && !isDone && !disable;

            // Compact summary rendered after the admin explicitly presses
            // "Complete Billing Setup". Re-opens the form via Edit Billing.
            if (isDone) {
              const rows: Array<[string, string]> = [];
              if (cfg.billing_type === "monthly") {
                rows.push(["Type", "Monthly"]);
                rows.push(["Fee", `$${Number(cfg.fee_amount || 0).toFixed(2)} per month`]);
                rows.push(["Billing day", `${dayOrdinal(cfg.billing_day)} of every month`]);
                rows.push(["Starts", dayjs(cfg.billing_start_date).format("MMM D, YYYY")]);
                rows.push([
                  "First month",
                  cfg.first_month_proration === "prorate"
                    ? "Prorate by remaining sessions"
                    : cfg.first_month_proration === "next"
                      ? "Start next month"
                      : "Charge full month",
                ]);
              } else if (cfg.billing_type === "per_class") {
                rows.push(["Type", "Per Class"]);
                rows.push(["Fee", `$${Number(cfg.fee_amount || 0).toFixed(2)} per class`]);
                rows.push(["Starts", dayjs(cfg.billing_start_date).format("MMM D, YYYY")]);
              } else {
                rows.push(["Type", "Period-Based"]);
                rows.push([
                  "Period",
                  `${dayjs(cfg.period_start_date).format("MMM D, YYYY")} → ${dayjs(cfg.period_end_date).format("MMM D, YYYY")}`,
                ]);
                rows.push(["Total fee", `$${Number(cfg.period_total_fee || 0).toFixed(2)}`]);
                rows.push([
                  "Installments",
                  cfg.period_installment_count === 1
                    ? "1 (single upfront)"
                    : `${cfg.period_installment_count} installments`,
                ]);
              }
              rows.push([
                "Invoice handling",
                cfg.invoice_generation_preference === "now"
                  ? "Generate invoice now"
                  : "Add to next consolidated invoice",
              ]);

              return (
                <Box
                  key={c.id}
                  sx={{
                    position: "relative",
                    border: `1px solid ${alpha("#16a34a", 0.35)}`,
                    borderRadius: "14px",
                    p: { xs: 1.75, md: 2.25 },
                    bgcolor: alpha("#16a34a", 0.04),
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5, mb: 1.25, flexWrap: "wrap" }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: 0.4, mb: 0.25 }}>
                        Billing for
                      </Typography>
                      <Typography sx={{ fontSize: 15, fontWeight: 800 }}>{c.label}</Typography>
                      {(c.staff || c.schedule || c.mode) && (
                        <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
                          {[c.staff, c.schedule, c.mode].filter(Boolean).join(" · ")}
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Chip
                        size="small"
                        icon={<CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
                        label="Billing setup completed"
                        sx={{
                          height: 24, fontSize: 11, fontWeight: 700,
                          bgcolor: alpha("#16a34a", 0.14),
                          color: "#15803d",
                          border: `1px solid ${alpha("#16a34a", 0.35)}`,
                        }}
                      />
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<EditOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                        onClick={() => onChange(c.id, { is_completed: false })}
                        disabled={disable}
                        sx={{ textTransform: "none", fontSize: 12, fontWeight: 700 }}
                      >
                        Edit Billing
                      </Button>
                    </Stack>
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gap: 0.75,
                      gridTemplateColumns: { xs: "1fr", sm: "auto 1fr" },
                      columnGap: 2,
                      alignItems: "baseline",
                    }}
                  >
                    {rows.map(([k, v]) => (
                      <React.Fragment key={k}>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: 0.3 }}>
                          {k}
                        </Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>{v}</Typography>
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              );
            }

            return (
              <Box
                key={c.id}
                sx={{
                  position: "relative",
                  border: `1px solid ${alpha(complete ? "#16a34a" : theme.palette.primary.main, complete ? 0.25 : 0.15)}`,
                  borderRadius: "14px",
                  p: { xs: 1.75, md: 2.25 },
                  bgcolor: "rgba(15,23,42,0.015)",
                  transition: "border-color .15s, box-shadow .15s",
                }}
              >
                {/* ── Billing V2 panel: shows what will actually be billed for
                    this (student, class) based on the student's billing type
                    and the class's default fee. When the student is on
                    monthly billing this panel makes it clear that the class
                    fee is NOT billed separately, so the admin knows the
                    legacy fields below are ignored. When on class-wise, the
                    panel is authoritative and the fee below is superseded
                    by the class default fee — the admin does not need to
                    type it again. ── */}
                <Box sx={{ mb: 1.5 }}>
                  <AssignmentBillingV2Panel
                    studentId={studentId}
                    classId={c.id}
                    classLabel={c.label}
                    billingStartDate={cfg.billing_start_date || null}
                  />
                </Box>

                {/* ── Class context banner ── */}
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5, mb: 1.5, flexWrap: "wrap" }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: 0.4, mb: 0.25 }}>
                      Billing for
                    </Typography>
                    <Typography sx={{ fontSize: 15, fontWeight: 800 }}>{c.label}</Typography>
                    {(c.staff || c.schedule || c.mode) && (
                      <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
                        {[c.staff, c.schedule, c.mode].filter(Boolean).join(" · ")}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Chip
                      size="small"
                      icon={complete
                        ? <CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />
                        : <ErrorOutlineIcon sx={{ fontSize: "14px !important" }} />}
                      label={complete ? "Ready to complete" : "Billing incomplete"}
                      sx={{
                        height: 22, fontSize: 10.5, fontWeight: 700,
                        bgcolor: alpha(complete ? "#16a34a" : "#f59e0b", 0.12),
                        color: complete ? "#16a34a" : "#b45309",
                        border: `1px solid ${alpha(complete ? "#16a34a" : "#f59e0b", 0.3)}`,
                      }}
                    />
                    {onApplyToAll && classes.length > 1 && (
                      <Button
                        size="small" variant="text" disabled={disable || !complete}
                        onClick={() => setConfirmApplyFrom(cfg)}
                        sx={{ textTransform: "none", fontSize: 11.5 }}
                      >
                        Apply to all
                      </Button>
                    )}
                  </Stack>
                </Box>

                {/* ── Billing Type ── */}
                <Box sx={{ mb: 1.5 }}>
                  <Typography id={`billing-type-label-${c.id}`} sx={{ fontSize: 11, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", mb: 0.5 }}>
                    Billing Type
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={cfg.billing_type}
                    aria-labelledby={`billing-type-label-${c.id}`}
                    onChange={(_, v) => v && onChange(c.id, { billing_type: v })}
                    disabled={disable}
                    sx={{ flexWrap: "wrap", "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, px: 1.75 } }}
                  >
                    <ToggleButton value="monthly">Monthly</ToggleButton>
                    <ToggleButton value="per_class">Per Class</ToggleButton>
                    <ToggleButton value="period_based">Period-Based</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* ── Fields (2-col on md+) ── */}
                <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
                  {cfg.billing_type !== "period_based" && (
                    <>
                      <TextField
                        size="small"
                        label={cfg.billing_type === "monthly" ? "Monthly Fee ($)" : "Fee per Class ($)"}
                        value={cfg.fee_amount}
                        onChange={(e) => onChange(c.id, { fee_amount: e.target.value.replace(/[^0-9.]/g, "") })}
                        error={feeInvalid}
                        helperText={feeInvalid ? "Enter a valid amount (>= 0)." : Number(cfg.fee_amount) === 0 && cfg.fee_amount !== "" ? "Free class — no invoice charge" : " "}
                        disabled={disable}
                        required
                      />
                      {/* Date pickers use the AppDatePicker `label` prop
                          rather than an external uppercase mini-header so the
                          field height matches the sibling MUI TextField (fee),
                          fixing the "date field floats too high with a big
                          gap" alignment bug. */}
                      <AppDatePicker
                        label="Billing Start Date"
                        value={cfg.billing_start_date}
                        onChange={(v) => onChange(c.id, { billing_start_date: v || dayjs().format("YYYY-MM-DD") })}
                        ariaLabel="Billing start date"
                        helperText=" "
                        fullWidth
                      />
                    </>
                  )}

                  {cfg.billing_type === "period_based" && (
                    <>
                      <AppDatePicker
                        label="Period Start"
                        value={cfg.period_start_date}
                        onChange={(v) => onChange(c.id, { period_start_date: v || dayjs().format("YYYY-MM-DD") })}
                        ariaLabel="Period start date"
                        helperText=" "
                        fullWidth
                      />
                      <AppDatePicker
                        label="Period End"
                        value={cfg.period_end_date}
                        onChange={(v) => onChange(c.id, { period_end_date: v || dayjs().add(3, "month").format("YYYY-MM-DD") })}
                        ariaLabel="Period end date"
                        helperText=" "
                        fullWidth
                      />
                      <TextField
                        size="small"
                        label="Total Period Fee ($)"
                        value={cfg.period_total_fee}
                        onChange={(e) => onChange(c.id, { period_total_fee: e.target.value.replace(/[^0-9.]/g, "") })}
                        error={periodTotalInvalid}
                        helperText={
                          periodTotalInvalid
                            ? "Enter a valid amount (> 0)."
                            : perInstallment !== null && cfg.period_installment_count > 1
                              ? `≈ $${perInstallment.toFixed(2)} per installment`
                              : "Total tuition for the whole period."
                        }
                        disabled={disable}
                        required
                      />
                      <TextField
                        size="small"
                        select
                        label="Installments"
                        value={cfg.period_installment_count}
                        onChange={(e) => onChange(c.id, { period_installment_count: Number(e.target.value) })}
                        helperText={cfg.period_installment_count === 1 ? "Single upfront invoice for the whole period." : `${cfg.period_installment_count} evenly-split invoices.`}
                        disabled={disable}
                        SelectProps={{
                          MenuProps: {
                            PaperProps: { style: { maxHeight: 320 } },
                            anchorOrigin: { vertical: "bottom", horizontal: "left" },
                            transformOrigin: { vertical: "top", horizontal: "left" },
                          },
                        }}
                      >
                        {Array.from({ length: 24 }, (_, i) => i + 1).map((n) => (
                          <MenuItem key={n} value={n}>
                            {n === 1 ? "1 (single upfront)" : `${n} installments`}
                          </MenuItem>
                        ))}
                      </TextField>
                    </>
                  )}

                  {cfg.billing_type === "monthly" && (
                    <>
                      <TextField
                        size="small"
                        select
                        label="Billing Day (of month)"
                        value={cfg.billing_day}
                        onChange={(e) => onChange(c.id, { billing_day: Number(e.target.value) })}
                        helperText={`Selected: ${dayOrdinal(cfg.billing_day)} of every month.`}
                        disabled={disable}
                        SelectProps={{
                          renderValue: (v: any) => `${dayOrdinal(Number(v))} of every month`,
                          MenuProps: {
                            // Cap the dropdown so it never expands into a full
                            // page-height list — the previous native menu was
                            // rendering all 28 days at once and destroying the
                            // page alignment. anchorOrigin pins it directly to
                            // the field so it opens compact + below.
                            PaperProps: { style: { maxHeight: 320, width: 220 } },
                            anchorOrigin: { vertical: "bottom", horizontal: "left" },
                            transformOrigin: { vertical: "top", horizontal: "left" },
                          },
                        }}
                      >
                        {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                          <MenuItem key={d} value={d}>{dayOrdinal(d)}</MenuItem>
                        ))}
                      </TextField>
                      {/* First Month shares row 3 with Billing Day now
                          (no more forced full-width span) so the two right-
                          column fields don't leave a large empty gap. Radios
                          wrap cleanly on tablet/mobile via flexWrap. */}
                      <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", mb: 0.5 }}>
                          First Month
                        </Typography>
                        <RadioGroup
                          value={cfg.first_month_proration}
                          onChange={(e) => onChange(c.id, { first_month_proration: e.target.value as any })}
                          sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 0.25 }}
                        >
                          <FormControlLabel value="full" disabled={disable} control={<Radio size="small" />} label={<Typography sx={{ fontSize: 12.5 }}>Charge full month</Typography>} />
                          <FormControlLabel value="prorate" disabled={disable} control={<Radio size="small" />} label={<Typography sx={{ fontSize: 12.5 }}>Prorate remaining</Typography>} />
                          <FormControlLabel value="next" disabled={disable} control={<Radio size="small" />} label={<Typography sx={{ fontSize: 12.5 }}>Start next month</Typography>} />
                        </RadioGroup>
                      </Box>
                    </>
                  )}
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", mb: 0.5 }}>
                    Invoice Handling
                  </Typography>
                  <RadioGroup
                    row
                    value={cfg.invoice_generation_preference}
                    onChange={(e) => onChange(c.id, { invoice_generation_preference: e.target.value as any })}
                  >
                    <FormControlLabel value="now" disabled={disable} control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13 }}>Generate invoice now</Typography>} />
                    <FormControlLabel value="next" disabled={disable} control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13 }}>Add to next consolidated invoice</Typography>} />
                  </RadioGroup>
                </Box>

                {/* ── Action footer: Complete Billing Setup ── */}
                <Box
                  sx={{
                    mt: 1.75,
                    pt: 1.5,
                    borderTop: "1px dashed rgba(15,23,42,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography sx={{ fontSize: 11.5, color: "text.secondary", flex: 1, minWidth: 200 }}>
                    {complete
                      ? "Review the numbers above, then click Complete Billing Setup to lock this class's billing."
                      : "Fill in the required fields to enable the Complete button."}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!canComplete}
                    onClick={() => onChange(c.id, { is_completed: true })}
                    startIcon={<LockOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: "10px",
                      px: 2,
                    }}
                  >
                    Complete Billing Setup
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Stack>

        {/* ── Live invoice preview ── */}
        <Box
          role="status"
          aria-live="polite"
          sx={{
            mt: 2.5, p: 2, borderRadius: "12px",
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            border: `1px dashed ${alpha(theme.palette.primary.main, 0.35)}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <ReceiptLongIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Invoice Preview</Typography>
            {isPreviewing && <CircularProgress size={14} sx={{ ml: 1 }} />}
          </Box>
          {previewError ? (
            <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", color: "#b45309" }}>
              <WarningAmberIcon sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: 12 }}>{previewError}</Typography>
            </Box>
          ) : previewData.lines.length === 0 ? (
            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
              No immediate invoice — every class is set to “Add to next consolidated invoice”, or fees are still incomplete.
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              {previewData.lines.map((l, i) => (
                <Box key={i} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                  <Typography sx={{ fontSize: 12.5 }}>{l.description}</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>${l.total.toFixed(2)}</Typography>
                </Box>
              ))}
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Invoice Total</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>${previewData.total.toFixed(2)}</Typography>
              </Box>
            </Stack>
          )}
        </Box>
      </Box>

      {/* ── Apply-to-all confirmation ── */}
      {confirmApplyFrom && (
        <Box
          role="dialog"
          aria-modal="true"
          onClick={() => setConfirmApplyFrom(null)}
          sx={{
            position: "fixed", inset: 0, bgcolor: "rgba(15,23,42,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1400,
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              bgcolor: "#fff", borderRadius: "14px", p: 3, maxWidth: 420, width: "90%",
              boxShadow: "0 20px 60px rgba(15,23,42,0.25)",
            }}
          >
            <Typography sx={{ fontSize: 15, fontWeight: 800, mb: 0.5 }}>Apply this billing setup to all selected classes?</Typography>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 2 }}>
              This will overwrite the current billing configuration on the {classes.length - 1} other selected {classes.length - 1 === 1 ? "class" : "classes"}.
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={() => setConfirmApplyFrom(null)} sx={{ textTransform: "none" }}>
                Cancel
              </Button>
              <Button
                size="small" variant="contained"
                onClick={() => {
                  onApplyToAll?.(confirmApplyFrom);
                  setConfirmApplyFrom(null);
                }}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Apply to all
              </Button>
            </Stack>
          </Box>
        </Box>
      )}
    </Wrapper>
  );
}
